import { jsx as _jsx } from "react/jsx-runtime";
import { Children, cloneElement, isValidElement, useContext, } from 'react';
import FloatingFavoritesButton from '../FloatingFavoritesButton/FloatingFavoritesButton';
import clsx from 'clsx';
import useStyles from './MainContent.styles';
import { useComponentLoading } from './useComponentLoading';
import { SiteContext } from '../../utils/common';
import { PRODUCTDETAIL } from '../../utils/constants';
import { useRouter } from 'next/router';
import { useTheme, useMediaQuery } from '@mui/material';
import { usePdpV2ExperienceGate } from '../../utils/usePdpV2ExperienceGate';
const MainContent = (props) => {
    const { classes } = useStyles();
    const counter = {};
    const { centreContent, isFloatingFavoritesEnabled } = props;
    const { asPath } = useRouter();
    const { subject } = useContext(SiteContext);
    // Hoist the V2 gate here so it fires once per MainContent render instead of
    // once per child component (MainContentComponent is rendered N times).
    const product = subject === null || subject === void 0 ? void 0 : subject.product;
    const isProductEligibleForPdpV2 = !!product && !product.isFuneral && !product.isInternational && !product.isOverseas;
    const { experience: pdpV2Experience } = usePdpV2ExperienceGate(isProductEligibleForPdpV2);
    const isPdpV2FloatingOffset = asPath.startsWith('/product/') && pdpV2Experience !== 'control';
    return (_jsx("div", { "data-component": "MainContent", className: clsx(classes.mainContent, centreContent === true && classes.centreContent, centreContent === 'small' && classes.centreContentSmall, centreContent === 'extraSmall' && classes.centreContentExtraSmall), "data-hj-ignore-attributes": true, children: Children.map(props.children, (child, index) => {
            var _a, _b;
            if (!isValidElement(child)) {
                return child;
            }
            const type = ((_b = (_a = child.props) === null || _a === void 0 ? void 0 : _a.component) === null || _b === void 0 ? void 0 : _b['uiType']) || 'unknown';
            counter[type] = (counter[type] || 0) + 1;
            const dataTestId = `${type}-${counter[type]}`;
            return (_jsx(MainContentComponent, { dataTestId, type, index, component: child, isFloatingFavoritesEnabled, isPdpV2FloatingOffset }));
        }) }));
};
// Helper function to determine component type categories
const getComponentTypeFlags = (type) => ({
    isMyAccountTitle: type === 'MyAccountWelcomeTitle',
    isMyAccountCards: type === 'MyAccountCards',
    isMyAccountReminders: type === 'MyAccountReminders' || type === 'MyAccountRecentOrdersAndDeliveryPass',
    isTitle: type === 'Title' || type === 'NoSearchResultsHeader',
    pdpSpacing: type === PRODUCTDETAIL,
    homeGallerySpacing: type === 'FfHomeHeroBannerGallery',
    linkGallery: type === 'LinkGallery',
    blogPage: ['BlogCategoryLinkGallery', 'BlogHeroBanner', 'BlogArticleContent', 'BlogAuthor'].includes(type),
});
// Helper function to determine spacing conditions
const getSpacingConditions = (type, index, isFloatingFavoritesEnabled) => {
    const heroTypes = ['HeroBanner', 'HomeHeroBanner', 'FfHomeHeroBannerGallery'];
    const pdpSpacing = type === PRODUCTDETAIL;
    return {
        topSpacing: !pdpSpacing && (!index || (!isFloatingFavoritesEnabled && index === 1)) && !heroTypes.includes(type),
        homeGallerySpacing: !index && type === 'FfHomeHeroBannerGallery',
    };
};
// Helper function to build className entries for the FloatingFavoritesButton wrapper
const getFloatingFavoritesClasses = (classes, isPDP, isPdpV2FloatingOffset, applicationPagesOnly) => [
    isPDP && classes.floatingButtonNoPadding,
    isPDP && isPdpV2FloatingOffset && classes.floatingButtonPdpV2Offset,
    !isPDP && !applicationPagesOnly,
];
// Helper function to build className array
const buildClassNames = (classes, typeFlags, spacing, options) => {
    const { isFloatingFavoritesButton, isPDP, isFlyingFlowers, applicationPagesOnly, noPadding, noVerticalPadding, isCheckoutPage, isPdpV2FloatingOffset, } = options;
    if (isFloatingFavoritesButton) {
        return getFloatingFavoritesClasses(classes, isPDP, isPdpV2FloatingOffset, applicationPagesOnly);
    }
    return [
        // Standard component classes
        classes.componentContainer,
        isFlyingFlowers && typeFlags.pdpSpacing && classes.pdpTopSpacing,
        isFlyingFlowers && !applicationPagesOnly && spacing.topSpacing && classes.topSpacing,
        isFlyingFlowers && isCheckoutPage && spacing.topSpacing && classes.checkoutTopSpacing,
        // Specific component type classes
        spacing.homeGallerySpacing && classes.homeHeroGallerySpacing,
        typeFlags.linkGallery && classes.componentContainer,
        typeFlags.isMyAccountTitle && classes.myAccountTitlePadding,
        typeFlags.isMyAccountCards && classes.myAccountCardPadding,
        typeFlags.isTitle && classes.titlePadding,
        typeFlags.isMyAccountReminders && classes.myAccountRemindersPadding,
        typeFlags.blogPage && !isFlyingFlowers && classes.blogCategoryLinkGallery,
        noPadding && classes.noPadding,
        noVerticalPadding && classes.noVerticalPadding,
    ];
};
const MainContentComponent = ({ component, dataTestId, type, index, isFloatingFavoritesEnabled, isPdpV2FloatingOffset, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const theme = useTheme();
    const { classes } = useStyles();
    const router = useRouter();
    const { pathname, asPath } = router;
    const { isFlyingFlowers } = useContext(SiteContext);
    const typeFlags = getComponentTypeFlags(type);
    const spacing = getSpacingConditions(type, index, isFloatingFavoritesEnabled);
    const isFloatingFavoritesButton = isValidElement(component) && component.type === FloatingFavoritesButton;
    const isPDP = asPath.startsWith('/product/');
    const isCategoryPage = asPath.startsWith('/category/');
    const isCheckoutPage = asPath.startsWith('/checkout');
    // Determine if the page is an application page (excluding Flying Flowers)
    const applicationPagesOnly = isFlyingFlowers ? false : asPath.startsWith('/page/') || pathname === '/';
    const [isLoading, onLoaded] = useComponentLoading(type);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // For DynamicImage, check if it has content before rendering the container
    let noPadding = false;
    if (type === 'DynamicImage') {
        const componentData = (_b = (_a = component.props) === null || _a === void 0 ? void 0 : _a.component) === null || _b === void 0 ? void 0 : _b.data;
        const hasDesktopImage = (_d = (_c = componentData === null || componentData === void 0 ? void 0 : componentData.desktopImage) === null || _c === void 0 ? void 0 : _c.image) === null || _d === void 0 ? void 0 : _d.url;
        const hasMobileImage = (_f = (_e = componentData === null || componentData === void 0 ? void 0 : componentData.mobileImage) === null || _e === void 0 ? void 0 : _e.image) === null || _f === void 0 ? void 0 : _f.url;
        noPadding = (isMobile && !hasMobileImage) || (!isMobile && !hasDesktopImage);
    }
    const isCategoryAvailabilityCheckerContainer = !!isFlyingFlowers &&
        isCategoryPage &&
        (type === 'AvailabilityChecker' || ((_g = component === null || component === void 0 ? void 0 : component.props) === null || _g === void 0 ? void 0 : _g['data-testid']) === 'category-availability-section-wrapper');
    return (_jsx("div", { "data-testid": dataTestId, "data-loading": isLoading || undefined, className: clsx(buildClassNames(classes, typeFlags, spacing, {
            isFloatingFavoritesButton,
            isPDP,
            isFlyingFlowers,
            applicationPagesOnly,
            noPadding,
            noVerticalPadding: isCategoryAvailabilityCheckerContainer,
            isCheckoutPage,
            isPdpV2FloatingOffset,
        })), children: cloneElement(component, { onLoaded }) }, type));
};
export default MainContent;
